import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { dataService } from '@/services/dataService';
import { Creator } from '@/types/entities';
import { DataTable, ColumnDef } from '@/components/ui/DataTable';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { EntityDrawer } from '@/components/ui/EntityDrawer';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { CopyButton } from '@/components/ui/CopyButton';
import { exportRowsToCsv } from '@/lib/exportCsv';
import { useI18n } from '@/i18n';
import { useToast } from '@/components/ui/Toast';
import { CreatorFormDrawer, CreatorFormValues } from './CreatorFormDrawer';

const VIEWS = ['all', 'active', 'need_followup', 'at_risk', 'pending_tiktok'] as const;
type ViewKey = (typeof VIEWS)[number];

function matchesView(c: Creator, view: ViewKey): boolean {
  switch (view) {
    case 'active': return c.status === 'active';
    case 'need_followup': return !!c.nextFollowup;
    case 'at_risk': return c.riskLevel === 'high' || c.status === 'at_risk';
    case 'pending_tiktok': return c.status === 'pending_tiktok';
    default: return true;
  }
}

export function CreatorsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const push = useToast();
  const [creators, setCreators] = useState<Creator[]>([]);
  const [selected, setSelected] = useState<Creator | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [view, setView] = useState<ViewKey>('all');
  type RiskFilter = 'all' | 'low' | 'medium' | 'high';
  const RISK_VALUES: RiskFilter[] = ['all', 'low', 'medium', 'high'];
  const [riskFilter, setRiskFilter] = useState<RiskFilter>('all');
  const [query, setQuery] = useState('');
  const [formTarget, setFormTarget] = useState<'new' | Creator | null>(searchParams.get('new') === '1' ? 'new' : null);
  const [confirmTarget, setConfirmTarget] = useState<{ creator: Creator; mode: 'archive' | 'delete' } | null>(null);

  function reload() {
    dataService.listCreators().then(setCreators);
  }
  useEffect(reload, []);

  const columns: ColumnDef<Creator>[] = [
    { key: 'id', header: 'ID', render: (c) => <span>{c.id} <CopyButton value={c.id} /></span> },
    { key: 'name', header: 'Name', render: (c) => c.fullName },
    { key: 'tiktok', header: 'TikTok', render: (c) => c.tiktokUsername },
    { key: 'status', header: 'Status', render: (c) => <StatusBadge entity="creator" statusKey={c.status} /> },
    { key: 'risk', header: 'Risk', render: (c) => <span className={`badge ${c.riskLevel === 'high' ? 'crit' : c.riskLevel === 'medium' ? 'warn' : 'good'}`}>{c.riskLevel}</span> },
    { key: 'nextFollowup', header: 'Next Follow-up', render: (c) => c.nextFollowup ?? '—' },
    {
      key: 'actions', header: '', render: (c) => (
        <span className="popover-anchor" onClick={(e) => e.stopPropagation()}>
          <button className="icon-btn" style={{ margin: 0 }} onClick={() => setMenuFor(menuFor === c.id ? null : c.id)}>⋯</button>
          {menuFor === c.id && (
            <>
              <div className="popover-backdrop" onClick={() => setMenuFor(null)} />
              <div className="popover-panel" style={{ width: 200 }}>
                <div className="popover-item" onClick={() => { setSelected(c); setMenuFor(null); }}>{t('rowMenu.view')}</div>
                <div className="popover-item" onClick={() => { setFormTarget(c); setMenuFor(null); }}>{t('rowMenu.edit')}</div>
                <div className="popover-item" onClick={() => { navigate(`/creators/${c.id}`); setMenuFor(null); }}>{t('rowMenu.openFull')}</div>
                <div className="popover-item" style={{ color: 'var(--warn)' }} onClick={() => { setConfirmTarget({ creator: c, mode: 'archive' }); setMenuFor(null); }}>{t('rowMenu.archive')}</div>
                <div className="popover-item" style={{ color: 'var(--crit)' }} onClick={() => { setConfirmTarget({ creator: c, mode: 'delete' }); setMenuFor(null); }}>{t('rowMenu.deleteDemo')}</div>
              </div>
            </>
          )}
        </span>
      ),
    },
  ];

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return creators
      .filter((c) => matchesView(c, view))
      .filter((c) => riskFilter === 'all' || c.riskLevel === riskFilter)
      .filter((c) => !q || c.id.toLowerCase().includes(q) || c.fullName.toLowerCase().includes(q) || c.tiktokUsername.toLowerCase().includes(q) || c.email.toLowerCase().includes(q));
  }, [creators, view, riskFilter, query]);

  async function handleFormSubmit(values: CreatorFormValues) {
    if (formTarget === 'new') {
      const created = await dataService.createCreator(values);
      push(`${t('toast.creatorAdded')} — ${created.id}`, 'success');
    } else if (formTarget) {
      await dataService.updateCreator(formTarget.id, values);
      push(t('toast.creatorUpdated'), 'success');
    }
    setFormTarget(null);
    setSearchParams({});
    reload();
  }

  async function handleConfirm() {
    if (!confirmTarget) return;
    if (confirmTarget.mode === 'archive') {
      await dataService.archiveCreator(confirmTarget.creator.id);
      push(t('toast.creatorArchived'), 'warn');
    } else {
      await dataService.deleteCreator(confirmTarget.creator.id);
      push(t('toast.demoRecordDeleted'), 'error');
    }
    setConfirmTarget(null);
    setSelected(null);
    reload();
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>{t('nav.creators')}</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => {
              exportRowsToCsv('creators', visible, [
                { key: 'id', header: 'ID', value: (c) => c.id },
                { key: 'name', header: 'Name', value: (c) => c.fullName },
                { key: 'tiktok', header: 'TikTok', value: (c) => c.tiktokUsername },
                { key: 'status', header: 'Status', value: (c) => c.status },
                { key: 'nextFollowup', header: 'Next Follow-up', value: (c) => c.nextFollowup ?? '' },
              ]);
              dataService.logExport('Creators');
            }}
          >
            {t('common.export')}
          </button>
          <button onClick={() => window.print()}>{t('common.print')}</button>
          <button className="btn-primary" onClick={() => setFormTarget('new')}>+ {t('quickCreate.creator')}</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {VIEWS.map((v) => (
          <button key={v} onClick={() => setView(v)} className={view === v ? 'btn-primary' : ''}>{t(`creatorViews.${v}`)}</button>
        ))}
        <select
          className="text-input"
          style={{ width: 140 }}
          value={riskFilter}
          onChange={(e) => {
            const val = e.target.value;
            if ((RISK_VALUES as string[]).includes(val)) setRiskFilter(val as RiskFilter);
          }}
        >
          <option value="all">{t('creatorViews.allRisk')}</option>
          <option value="low">low</option>
          <option value="medium">medium</option>
          <option value="high">high</option>
        </select>
        <input
          className="text-input"
          placeholder={t('common.search')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ marginInlineStart: 'auto', width: 220 }}
        />
      </div>

      <div className="card">
        {visible.length === 0 ? (
          <EmptyState text={t('empty.noCreators')} cta={t('quickCreate.creator')} onCta={() => setFormTarget('new')} />
        ) : (
          <DataTable columns={columns} rows={visible} onRowClick={setSelected} />
        )}
      </div>

      {selected && (
        <EntityDrawer title={selected.fullName} subtitle={`${selected.id} · ${selected.tiktokUsername}`} onClose={() => setSelected(null)}>
          <p><b>Phone:</b> {selected.phone}</p>
          <p><b>Email:</b> {selected.email}</p>
          <p><b>Country:</b> {selected.country} · {selected.emirate}</p>
          <p><b>Status:</b> <StatusBadge entity="creator" statusKey={selected.status} /></p>
          <p><b>Next action:</b> {selected.nextAction ?? '—'}</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button className="btn-primary" onClick={() => navigate(`/creators/${selected.id}`)}>{t('rowMenu.openFull')}</button>
            <button onClick={() => setFormTarget(selected)}>{t('rowMenu.edit')}</button>
          </div>
        </EntityDrawer>
      )}

      {formTarget && (
        <CreatorFormDrawer
          creator={formTarget === 'new' ? null : formTarget}
          onClose={() => { setFormTarget(null); setSearchParams({}); }}
          onSubmit={handleFormSubmit}
        />
      )}

      <ConfirmModal
        open={!!confirmTarget}
        title={confirmTarget?.mode === 'archive' ? t('confirm.archiveCreatorTitle') : t('confirm.deleteCreatorTitle')}
        recordName={confirmTarget ? `${confirmTarget.creator.id} — ${confirmTarget.creator.fullName}` : undefined}
        description={confirmTarget?.mode === 'archive' ? t('confirm.archiveCreatorDesc') : t('confirm.deleteCreatorDesc')}
        reversible={confirmTarget?.mode === 'archive'}
        tone={confirmTarget?.mode === 'delete' ? 'destructive' : 'normal'}
        confirmLabel={confirmTarget?.mode === 'archive' ? t('rowMenu.archive') : t('rowMenu.deleteDemo')}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
}

function EmptyState({ text, cta, onCta }: { text: string; cta: string; onCta: () => void }) {
  return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <div style={{ color: 'var(--text-3)', marginBottom: 12 }}>{text}</div>
      <button className="btn-primary" onClick={onCta}>+ {cta}</button>
    </div>
  );
}
